import type { Filter } from "@judo/model-api";
import { FilterOperationType } from "@judo/model-api";
import type { GridColType } from "@mui/x-data-grid";

/**
 * MUI X DataGrid built-in operator tokens used by this codebase.
 *
 * NOTE: These are NOT model values — they belong to the rendering layer
 * (`@mui/x-data-grid`'s `getGridDefaultColumnTypes()`). They cannot be
 * derived from `@judo/model-api`. The local union exists so a typo in
 * the operator-mapping table fails compilation instead of silently
 * producing a string DataGrid will reject at runtime.
 */
export type DataGridOperator =
	| "contains"
	| "equals"
	| "is"
	| "not"
	| "="
	| "!="
	| "<"
	| "<="
	| ">"
	| ">="
	| "before"
	| "onOrBefore"
	| "after"
	| "onOrAfter"
	| "isEmpty"
	| "isNotEmpty";

/**
 * Gets the MUI DataGrid column type for a Filter element.
 *
 * The underlying model may represent data types either with the bare name
 * (`NumericType`) or the prefixed EMF form (`data:NumericType`); both are
 * accepted and produce the same result.
 *
 * Same logic as getColumnType() in table-column-utils.ts, but operates on
 * Filter.attributeType instead of Column.attributeType.
 */
export function getFilterColumnType(filter: Filter): GridColType {
	const dataType = filter.attributeType?.dataType?.["@type"];
	if (!dataType) return "string";

	// Review refactor R5 — normalize the "data:" EMF prefix once so the
	// switch below has a single case per semantic type instead of a pair.
	const normalized = dataType.replace(/^data:/, "");

	switch (normalized) {
		case "NumericType":
			return "number";
		case "BooleanType":
			return "boolean";
		case "DateType":
			return "date";
		case "TimestampType":
			return "dateTime";
		case "EnumerationType":
			return "singleSelect";
		case "TimeType":
			// MUI DataGrid has no native "time" column type; surface as string
			return "string";
		default:
			return "string";
	}
}

/**
 * Maps a model Filter's defaultOperation + data type to the MUI DataGrid
 * operator string used in GridFilterItem.
 *
 * The returned operator flows through the existing table-filter-utils.ts →
 * build-query-customizer.ts pipeline unchanged.
 */
export function getFilterDataGridOperator(filter: Filter): DataGridOperator {
	const columnType = getFilterColumnType(filter);
	const operation = filter.defaultOperation;
	if (!operation) return getDefaultOperatorForType(columnType);
	return mapOperationToDataGridOperator(operation, columnType);
}

/**
 * Review refactor R5 — operator-to-DataGrid-operator lookup table.
 *
 * Boundary contract:
 *   keys   = `FilterOperationType` from `@judo/model-api` — model-derived,
 *            so adding a new variant in the model fails compilation here
 *            until the new key is handled.
 *   values = MUI X DataGrid built-in operator tokens (`DataGridOperator`)
 *            — belong to the rendering layer, NOT derivable from the model.
 *
 * Each model operation has a default DataGrid operator, plus optional
 * column-type-specific overrides (e.g. LESS becomes "before" for date
 * columns, "<" for everything else). This table replaces the previous
 * nested if/else ladder with a single data-driven resolution.
 */
interface OperatorMapping {
	/** Operator used when no column-type override applies. */
	default: DataGridOperator;
	/** Column-type-specific overrides (only keys present override the default). */
	overrides?: Partial<Record<GridColType, DataGridOperator>>;
}

const OPERATOR_TO_DATA_GRID: Record<FilterOperationType, OperatorMapping> = {
	LIKE: { default: "contains" },
	EQUAL: {
		default: "is",
		overrides: { number: "=", string: "equals" },
	},
	NOT_EQUAL: {
		default: "not",
		overrides: { number: "!=" },
	},
	LESS: {
		default: "<",
		overrides: { date: "before", dateTime: "before" },
	},
	LESS_OR_EQUAL: {
		default: "<=",
		overrides: { date: "onOrBefore", dateTime: "onOrBefore" },
	},
	GREATER: {
		default: ">",
		overrides: { date: "after", dateTime: "after" },
	},
	GREATER_OR_EQUAL: {
		default: ">=",
		overrides: { date: "onOrAfter", dateTime: "onOrAfter" },
	},
	IS_EMPTY: { default: "isEmpty" },
	IS_NOT_EMPTY: { default: "isNotEmpty" },
};

function mapOperationToDataGridOperator(operation: FilterOperationType, columnType: GridColType): DataGridOperator {
	const mapping = OPERATOR_TO_DATA_GRID[operation];
	// Defensive fallback: should be unreachable because `operation` is keyed by
	// the same union the table is keyed by. Kept so unit tests can exercise the
	// "unknown operation string" path without resorting to runtime-only casts.
	if (!mapping) return getDefaultOperatorForType(columnType);
	return mapping.overrides?.[columnType] ?? mapping.default;
}

function getDefaultOperatorForType(columnType: GridColType): DataGridOperator {
	switch (columnType) {
		case "number":
			return "=";
		case "dateTime":
			// Equality on millisecond-precision timestamps is not human-usable as a
			// starting filter value. "after" is the sensible default; same semantic
			// as the GREATER override for dateTime columns.
			return "after";
		case "boolean":
		case "date":
		case "singleSelect":
			return "is";
		default:
			return "contains";
	}
}
